import { DependencyList, useCallback, useEffect, useRef } from 'react';

/**
 * Stores the given function in a reference that updates when the given dependencies change.
 * The returned callback is stable (no dependencies, memonization never changes) and simply calls the referenced function.
 *
 * This is useful when we need to have a callback function that updates with dependencies, but should not cause
 * a re-render. Note however, that this can cause unexpected behaviour when called during rendering, and should only
 * be used for events/effects.
 *
 * One common use-case is for callbacks registered as eventHandlers where we do not want to re-register handlers
 * when memonizations change:
 *
 * @example
 * ```tsx
 * const handleBrowserEvent = useCallbackRef(() => {
 *   // This depends on `isConnected` which changes
 *   if (!isConnected) connect();
 * }, [isConnected, connect]);
 *
 * useEffect(() => {
 *   // Handle browser event is a ref, does not to be updated via dependency
 *   window.addEventListener('online', handleBrowserEvent);
 *   return () => window.removeEventListener('online', stableHandleBrowserEvent);
 * }, []);
 * ```
 *
 * @see https://stackoverflow.com/questions/55565444/how-to-register-event-with-useeffect-hooks
 */
export default function useStableCallback<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  dependencies: DependencyList,
): (...args: Parameters<T>) => ReturnType<T> {
  const callbackRef = useRef<T>(fn);

  // update ref with given dependencies
  useEffect(() => {
    callbackRef.current = fn;

    // We need to dynamically accept dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, ...dependencies]);

  // stable callback that simply calls the current function ref
  const callback = useCallback((...args: Parameters<T>): ReturnType<T> => {
    return callbackRef.current(...args);
  }, []);

  return callback;
}
